'use client';

/**
 * WalletHowItWorks — 3-step adoption flow.
 *
 * Anchors the wallet promise: customer scans → SUM detects platform →
 * one tap saves the card to their phone.
 */

import Link from 'next/link';
import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import StepCards, { StepCardItem } from '../_shared/sections/StepCards';
import { useTranslation } from '@/i18n/client';
import { HiArrowRight, HiCursorArrowRays, HiDevicePhoneMobile, HiQrCode } from 'react-icons/hi2';

type Props = { lang: Locale };

type StepKey = 'scan' | 'detect' | 'save';
type StepCopy = { num: string; title: string; description: string };

const STEP_NUMS: Record<StepKey, string> = {
  scan: '01',
  detect: '02',
  save: '03',
};

const STEP_ICONS: Record<StepKey, StepCardItem['Icon']> = {
  scan: HiQrCode,
  detect: HiDevicePhoneMobile,
  save: HiCursorArrowRays,
};

const KEYS: StepKey[] = ['scan', 'detect', 'save'];

const WalletHowItWorks = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const copyFor = (k: StepKey): StepCopy => ({
    num: STEP_NUMS[k],
    title: t(`Wallet/HowItWorks/${k}/title`),
    description: t(`Wallet/HowItWorks/${k}/body`),
  });

  const steps: StepCardItem[] = KEYS.map((k) => {
    const c = copyFor(k);
    return {
      key: k,
      num: c.num,
      title: c.title,
      description: c.description,
      Icon: STEP_ICONS[k],
      footer:
        k === 'scan' ? (
          <Link
            href={`/${lang}/loyalty-programs`}
            className="mono inline-flex items-center gap-1.5"
            style={{
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: '#22D3EE',
              textDecoration: 'none',
              alignSelf: 'flex-start',
            }}
          >
            {t('Wallet/HowItWorks/scan_link_label')}
            <HiArrowRight className="w-3 h-3" />
          </Link>
        ) : undefined,
    };
  });

  return (
    <section
      id="how-it-works"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0E1130',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="01" label={t('Wallet/HowItWorks/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('Wallet/HowItWorks/title1')} <span style={{ color: '#B4C1D8' }}>{t('Wallet/HowItWorks/title2')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('Wallet/HowItWorks/subtitle')}
        </p>

        <StepCards steps={steps} accent="#22D3EE" minCardWidth={260} showArrow />
      </div>
    </section>
  );
};

export default WalletHowItWorks;
